import { useQuery } from '@tanstack/react-query';
import { withNamespaces } from 'react-i18next';
import { RiCheckboxCircleLine, RiCloseCircleLine } from 'react-icons/ri';
import { toast } from 'react-toastify';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, Spinner } from 'reactstrap';
import ComplianceScreeningService from 'src/modules/3rd-party-management/apis/admin/compliance/screening/ScreeningService';

const ScreenConfirmationModal = ({ 
    t, 
    isOpen, 
    toggle, 
    onCompleted, 
    people,
    isBulkAction, 
    supplierId 
}) => {
    const peopleIds = isBulkAction ? people : [people[0]?.id];
    const selectedPeopleCount = people?.length;

    const startScreeningPersonQuery = useQuery({
        queryKey: [
            '3rd-party-management-kyb-compliance-screening-start',
            peopleIds,
            supplierId
        ],
        queryFn: async () => {
            const service = ComplianceScreeningService.getInstance();

            return await service.startScreeningPerson({
                supplier_id: supplierId,
                member_ids: peopleIds
            });
        },
        cacheTime: 10,
        refetchOnWindowFocus: false,
        enabled: false,
        retry: false,
        onSuccess: () => {
            toast(
                t('Screening completed successfully!'), 
                {
                    type: 'success'
                }
            );
            onCompleted && onCompleted();
        },
        onError: ({ response }) => {
            const {
                data: errorData
            } = response || {};

            if (errorData?.error === 'insufficient_credit_kyb') {
                toast(t('Unable to start screening due to insufficient KYB {{creditLabel}}. Please top up your balance and try again.', {
                    creditLabel: 'credit'
                }), {
                    type: "error"
                });
            }
            else {
                toast(t("Screening could not be completed. Please try again."), {
                    type: "error"
                });
            }
        }
    });

    const handleStartScreeningPerson = () => {
        startScreeningPersonQuery.refetch();
    }

    const renderMessageBody = () => {
        if (startScreeningPersonQuery.isFetched && startScreeningPersonQuery.isSuccess) {
            return (
                <div className='d-flex align-items-center justify-content-middle'>
                    <RiCheckboxCircleLine size={32} className='text-success me-1' />
                    <p className="text-success mb-0">
                        {t('Screening completed successfully!')}
                        <br />
                        {isBulkAction ? (
                            t('New findings for {{fullName}} have been added to the {{complianceType}} tab.', { fullName: people[0]?.fullName, complianceType: 'Compliance Screening' })
                        ) : (
                            t('New findings for the selected team members have been added to the {{complianceType}} tab.', {
                                complianceType: "Compliance Screening"
                            })   
                        )}
                    </p>
                </div>
            )
        }

        if (startScreeningPersonQuery.isFetched && startScreeningPersonQuery.isError) {
            const {
                data: errorData
            } = startScreeningPersonQuery.error?.response || {};

            return (
                <div className='d-flex align-items-center justify-content-middle'>
                    <RiCloseCircleLine size={32} className='text-danger me-1' />

                    <p className="text-danger mb-0">
                        {errorData?.error === 'insufficient_credit_kyb' ? (
                            t('Unable to start screening due to insufficient KYB {{creditLabel}}. Please top up your balance and try again.', {
                                creditLabel: 'credit'
                            })
                        ) : (
                            t('Screening could not be completed. Please try again.')
                        )}
                    </p>
                </div>
            )
        }

        return (
            <div>
                {isBulkAction ? (
                    <p>
                        {t('Are you sure you want to start screening {{count}} people?', { count: selectedPeopleCount })}
                    </p>
                ) : (
                    <p>
                        {t('You are about to start the screening process for')} <strong>{people[0]?.fullName}</strong>.
                    </p>
                )}

                <p>
                    {t('This will initiate a review based on the available data. Please make sure the information is correct before proceeding.')}
                </p>
            </div>
        )
    }

    const requestFailed = startScreeningPersonQuery.isFetched && startScreeningPersonQuery.isError;
    const requestSucceed = startScreeningPersonQuery.isSuccess;

    return (
        <Modal isOpen={isOpen} toggle={toggle}>
            <ModalHeader toggle={toggle}>
                {t('Confirm Screening')}
            </ModalHeader>

            <ModalBody>
                {renderMessageBody()}               
            </ModalBody>
            
            <ModalFooter>
                <Button 
                    color="secondary" 
                    onClick={toggle}
                    disabled={startScreeningPersonQuery.isFetching}
                >
                    {(requestFailed || requestSucceed) ? t('Close') : t('Cancel')}
                </Button>
                
                {!requestFailed && !requestSucceed && (
                    <Button
                        color="primary"
                        onClick={handleStartScreeningPerson}
                        disabled={startScreeningPersonQuery.isFetching}
                    >
                        {startScreeningPersonQuery.isFetching && (
                            <Spinner size="sm" className='me-1' />
                        )}
                        {t('Confirm')}
                    </Button>
                )}
            </ModalFooter>
        </Modal>
    );
}

export default withNamespaces()(
    ScreenConfirmationModal
);